import React, { useState, useEffect, createContext, useContext, useMemo } from "react";
import { createIntl, createIntlCache, RawIntlProvider, IntlConfig } from "react-intl";
import cookie from "js-cookie";
import useTranslation from "utils/hooks/useTranslation";
import getLocale from "./getLocale";
import getMessages from "./getMessages";
import { loadIntlPolyfills } from "./polyfills";

void loadIntlPolyfills();

type localeType = IntlConfig["locale"];
type messagesType = IntlConfig["messages"];
type timeZoneType = IntlConfig["timeZone"];

type TimeFormatOptions = {
  "24hour": {
    hour12: boolean;
    hours: "numeric" | "2-digit";
    minute: "numeric" | "2-digit";
  };
};

type CustomFormats = {
  time: TimeFormatOptions;
};

export type IFormatsType = CustomFormats;

interface ContextProps {
  setLocale: (locale: localeType, formats: IFormatsType, timeZone: timeZoneType) => void;
  locale: localeType;
  messages: messagesType;
  formats: IFormatsType;
  timeZone: timeZoneType;
  defaultLocale: localeType;
}

const LocaleContext = createContext<ContextProps | undefined>(undefined);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
};

const cache = createIntlCache();

const IntlProvider: React.FC<React.PropsWithChildren<Pick<ContextProps, "locale" | "messages" | "formats" | "timeZone" | "defaultLocale">>> = ({
  children,
  locale,
  messages,
  formats,
  timeZone,
  defaultLocale,
}) => {
  const [intl, setIntl] = useState(createIntl({ locale, messages, formats, timeZone, defaultLocale }, cache));

  const setLocale = async (nextLocale: localeType, nextFormats: IFormatsType, nextTimeZone: timeZoneType) => {
    const nextMessages = await getMessages(nextLocale);
    setIntl(
      createIntl(
        {
          locale: nextLocale,
          messages: nextMessages,
          formats: nextFormats,
          timeZone: nextTimeZone,
          defaultLocale,
        },
        cache
      )
    );
    cookie.set("locale", nextLocale, { expires: 365 });
  };

  useEffect(() => {
    setIntl(createIntl({ locale, messages, formats, timeZone, defaultLocale }, cache));
  }, [locale, messages, formats, timeZone, defaultLocale]);

  const providerValue = useMemo(
    () => ({
      setLocale,
      locale,
      messages,
      formats,
      timeZone,
      defaultLocale,
    }),
    [setLocale, locale, messages, formats, timeZone, defaultLocale]
  );

  return (
    <LocaleContext.Provider value={providerValue}>
      <RawIntlProvider value={intl}>{children}</RawIntlProvider>
    </LocaleContext.Provider>
  );
};

export default IntlProvider;

const convertToIntlLocale = (input) => {
  const languageMap = {
    "pt-BR": "pt-br",
    "en-US": "en",
    "es-MX": "es",
  };
  return languageMap[input] || input;
};

export const translate = async (id: string) => {
  try {
    const getDefaultLocale = await getLocale();
    const defaultLocale = convertToIntlLocale(getDefaultLocale);
    const messages = await getMessages(defaultLocale);
    const intl = createIntl({ locale: defaultLocale, messages }, cache);
    return intl.formatMessage({ id });
  } catch (error) {
    console.error(useTranslation({ id: "error_loading_translation" }), error);
    return id;
  }
};
